// Browser-side equivalent of android/.../data/ApiService.kt.
// Same `/api/v1/devices/*` endpoints the Android player consumes — keeps
// the backend a single source of truth and the dashboard treats web
// players exactly like Android players.

const BACKEND = (import.meta.env.VITE_BACKEND_URL || '').replace(/\/$/, '')
const BASE = `${BACKEND}/api/v1`

export const BASE_URL = BACKEND

export interface RegisterRequest {
  name: string
  device_model?: string | null
  os_version?: string | null
  app_version?: string | null
  mac_address?: string | null
  device_uid?: string | null
  device_kind?: string | null
}

export interface RegisterResponse {
  device_token: string
  screen_id: number
}

export interface HeartbeatRequest {
  device_token: string
  latitude?: number | null
  longitude?: number | null
  is_screen_live?: boolean | null
  device_model?: string | null
  os_version?: string | null
  app_version?: string | null
  ip_address?: string | null
  // Connection telemetry (dashboard's screen info drawer). timezone: IANA
  // zone. latency_ms: RTT this client measured on its PREVIOUS heartbeat.
  // uptime_seconds is deliberately absent — browsers can't see OS uptime,
  // so the web player never reports it (dashboard shows N/A).
  timezone?: string | null
  latency_ms?: number | null
  // Battery Status API readings — Chromium only; omitted (null) elsewhere.
  battery_pct?: number | null
  battery_charging?: boolean | null
}

export interface HeartbeatResponse {
  ok: boolean
  on_time?: string | null
  off_time?: string | null
  // License lifecycle. See backend devices.py HeartbeatResponse for the
  // canonical doc — hours_until_disconnect is the seed value the monotonic
  // countdown decrements from until the player must self-unpair.
  disconnect_at?: string | null
  hours_until_disconnect?: number | null
  server_now?: string | null
}

export interface ContentItem {
  id: number
  type: 'menu' | 'visual' | 'media' | string
  name: string
  duration?: number | null
  url?: string | null
  render_url?: string | null
  is_interactive?: boolean
  // Raw external URL for an interactive-link menu. Used to render a "scan to
  // open" QR fallback when the target site refuses iframing (X-Frame-Options /
  // CSP). Null for first-party rendered menus. See backend ContentItem.
  link_url?: string | null
  // Per-row mute for playlist videos. The player mutes a video when the screen
  // is muted OR this is set. See backend ContentItem (devices.py).
  muted?: boolean
  // Pre-cropped aspect variants — backend Pillow generates one per ratio
  // (16:9 / 9:16 / 1:1) on image upload. The player picks the entry matching
  // its screen orientation and falls back to `url` when the key is missing
  // (legacy rows, GIFs, videos, PDFs).
  variants?: Record<string, string> | null
}

// One region of a zoned layout. Grid placement is 1-indexed (CSS grid-column
// / grid-row start+span); the player seats the zone into the layout grid and
// runs its own rotation over `items`. See backend ContentZone (devices.py).
export interface ContentZone {
  id: string
  name: string
  col: number
  row: number
  col_span: number
  row_span: number
  items: ContentItem[]
}

// A zoned layout assigned to the screen. Carries resolved grid geometry
// (track counts + per-track fractional weights) so the player builds the grid
// without the template catalogue, plus per-zone content streams. At most one
// is sent (overwrite rules). See backend ContentLayout (devices.py).
export interface ContentLayout {
  id: number
  name: string
  template: string
  cols: number
  rows: number
  column_weights: number[]
  row_weights: number[]
  zones: ContentZone[]
}

// Background-audio track served alongside the visual rotation. Players
// (web + android) play these in parallel with whichever item is on screen.
// See backend ContentAudioItem (devices.py) for the canonical doc.
export interface ContentAudioItem {
  id: number
  name: string
  url: string
  volume: number   // 0..100 — players apply as 0..1
  loop: boolean    // per-track infinite loop; overridden by audio_loop_all
}

// Interactive attract overlay assigned to the screen — an image/video rotation
// painted ON TOP of the normal content, dismissed on touch and re-shown after
// dwell_seconds. All fields ride in the (cached) manifest so the touch + dwell
// behaviour works fully offline. See backend StopScreenManifest.
export interface StopScreenManifest {
  id: number
  items: ContentItem[]
  dwell_seconds: number
  show_cta: boolean
  cta_text: string
  cta_position: string
  touch_mode: 'anywhere' | 'button_only' | string
  // CTA button appearance (see backend StopScreen model). Optional so older
  // cached manifests fall back to the defaults.
  cta_variant?: 'button' | 'pill' | 'circle' | 'hand' | 'arrow' | string
  cta_style?: 'solid' | 'glassy' | 'gradient' | 'outline' | string
  cta_text_color?: string
  cta_bg_color?: string
  cta_size?: 'sm' | 'md' | 'lg' | string
}

export interface ContentResponse {
  screen_id: number
  // Operator-facing identifiers. The drawer surfaces these instead of the raw
  // numeric screen_id (sensitive). Both nullable — name is set once paired, and
  // branch_name is null for a screen not assigned to a branch.
  screen_name?: string | null
  branch_name?: string | null
  // Two-axis orientation. `orientation_type` picks the content aspect ratio
  // (16:9 for horizontal, 9:16 for vertical); `rotation_degrees` rotates the
  // output around centre at 0 / 90 / 180 / 270. The player composes the
  // two to know both how to frame content and which way it should render.
  orientation_type: 'horizontal' | 'vertical'
  rotation_degrees: 0 | 90 | 180 | 270
  // Display settings — sent flattened on the wire (see backend's
  // ContentResponse in routes/devices.py). Defaults match DisplaySettings.
  scaling?: 'default' | 'fit'
  audio_unmuted?: boolean
  // Mutes the video content's own audio track independently of the master
  // audio channel. Absent on older manifests → not muted.
  video_muted?: boolean
  // Master audio level 0–100, applied when the screen is unmuted. Absent on
  // older manifests → treated as full volume (100).
  volume?: number
  // Content-item transition (catalog key) + speed. Absent / 'default' → the
  // built-in fade crossfade. speed scales the animation duration.
  transition?: string | null
  transition_speed?: 'slow' | 'normal' | 'fast'
  // Operator setting: hide the on-screen fullscreen/install overlay and the
  // menu's fullscreen row. Absent on older manifests -> false (shown), so an
  // un-upgraded backend never accidentally hides the only way into fullscreen.
  hide_player_controls?: boolean
  on_time?: string | null
  off_time?: string | null
  is_paired: boolean
  is_active: boolean
  pairing_code?: string | null
  items: ContentItem[]
  // Zoned layout, present only when one is published to this screen. Null for
  // plain playlist / menu / media content. When set, the player renders the
  // grid from this; `items` stays empty so non-zone code paths show nothing.
  layout?: ContentLayout | null
  // Interactive attract overlay layer. Null when none is active/scheduled-in.
  stop_screen?: StopScreenManifest | null
  // Background audio (flattened across the screen's playlists). Sequence
  // loop wins over per-track loop at playback. See ContentAudioItem above.
  audio?: ContentAudioItem[]
  audio_loop_all?: boolean
  // Active channel schedule windows — the start/end of the currently-playing
  // occupant of each channel. Null on both ends = a permanent (unscheduled)
  // occupant. The operator drawer surfaces these inline next to each content
  // piece so a screen can tell when its content is scheduled to start / end.
  content_start_at?: string | null
  content_end_at?: string | null
  audio_start_at?: string | null
  audio_end_at?: string | null
  stop_start_at?: string | null
  stop_end_at?: string | null
  // Cache lifecycle (see Screen model). null cache_ttl_hours → use the
  // player default (CACHE_TTL_DEFAULT_HOURS in usePlayerState.ts).
  cache_ttl_hours?: number | null
  cache_evict_at?: string | null   // ISO-8601 UTC
  // License lifecycle — same shape as HeartbeatResponse so the monotonic
  // countdown can be re-seeded from either path.
  disconnect_at?: string | null
  hours_until_disconnect?: number | null
  server_now?: string | null
  // Enterprise-only license-expiry fallback (see backend's
  // app/services/license_fallback.py). Non-null ONLY when this screen's
  // license has expired but the org's plan tolerates it instead of the
  // standard unpair+404 — `items` is empty in that response and the player
  // renders this image full-bleed instead (see LicenseFallbackScreen).
  fallback_image_url?: string | null
}

// Tiny error wrapper so the caller can distinguish 404 (screen removed) from
// other failures without hand-parsing the response.
export class ApiError extends Error {
  status: number
  retryAfterMs: number | null
  // Diagnostic disconnect code from the X-Disconnect-Code response header
  // (set by the backend on license-expiry 404s). null when absent.
  disconnectCode: number | null
  constructor(status: number, message: string, retryAfterMs: number | null = null, disconnectCode: number | null = null) {
    super(message)
    this.status = status
    this.retryAfterMs = retryAfterMs
    this.disconnectCode = disconnectCode
  }
}

// Parse the X-Disconnect-Code header (backend disconnect diagnostic) off a response.
function readDisconnectCode(res: Response): number | null {
  const raw = res.headers.get('X-Disconnect-Code')
  const n = raw ? Number(raw) : NaN
  return Number.isFinite(n) ? n : null
}

async function send<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: object,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    let detail = ''
    try { detail = (await res.json())?.detail ?? '' } catch { /* ignore */ }
    const retryAfter = res.headers.get('Retry-After')
    const retryAfterMs = retryAfter && Number.isFinite(Number(retryAfter))
      ? Math.max(0, Number(retryAfter) * 1000)
      : null
    throw new ApiError(res.status, detail || `HTTP ${res.status}`, retryAfterMs, readDisconnectCode(res))
  }
  return res.json() as Promise<T>
}

export const deviceApi = {
  register: (req: RegisterRequest) =>
    send<RegisterResponse>('POST', '/devices/register', req),
  heartbeat: (req: HeartbeatRequest) =>
    send<HeartbeatResponse>('POST', '/devices/heartbeat', req),
  getContent: async (token: string): Promise<ContentResponse> => {
    const res = await fetch(`${BASE}/devices/content`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'X-Device-Token': token,
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })
    if (!res.ok) {
      let detail = ''
      try { detail = (await res.json())?.detail ?? '' } catch { /* ignore */ }
      const retryAfter = res.headers.get('Retry-After')
      const retryAfterMs = retryAfter && Number.isFinite(Number(retryAfter))
        ? Math.max(0, Number(retryAfter) * 1000)
        : null
      throw new ApiError(res.status, detail || `HTTP ${res.status}`, retryAfterMs, readDisconnectCode(res))
    }
    return res.json() as Promise<ContentResponse>
  },
  reportError: (token: string, error?: string | null) =>
    send<{ ok: boolean }>('POST', '/devices/report-error', { device_token: token, error }),
  reportRetry: (token: string, itemName?: string | null) =>
    send<{ ok: boolean }>('POST', '/devices/report-retry', { device_token: token, item_name: itemName }),
  reportCommandEvent: (token: string, commandId: string, command: string, status: string, detail?: string) =>
    send<{ ok: boolean }>('POST', '/devices/command-event', {
      device_token: token, command_id: commandId, command, status, detail,
    }),
}

// Resolve a URL that may be relative (`/uploads/...`) against the backend host.
export function resolveUrl(url?: string | null): string | null {
  if (!url) return null
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  return `${BACKEND}${url.startsWith('/') ? '' : '/'}${url}`
}
